import {
  Checkbox,
  NumberInput,
  Select,
  Textarea,
  TextInput,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import type { Field } from "../../../../types";
import DynamicInputPair from "./DynamicInputPair/DynamicInputPair";
import DifferentDaycares from "../Custom/DifferentDaycares/DifferentDaycares";
import { tiles } from "../../../../utils/configs/organizerConfig";
import {
  MaskedPasswordInput,
  MaskedTextInput,
} from "../../../../components/CustomInputs/CustomInputs";
import "react-datepicker/dist/react-datepicker.css";
import * as TablerIcons from "@tabler/icons-react";
import type { ReactNode } from "react";
import "@mantine/dates/styles.css";

type IconName = keyof typeof TablerIcons;

// Define a type for the option
type ChoiceOption = string | { value: string; icon?: IconName };

type FieldConfig = {
  field: Field;
  value: any;
  className?: string;
  popoverContent?: ReactNode;
};

type HandlerConfig = {
  groupIndex: number;
  handleFieldChange: Function;
  disabled: boolean;
  errorMessage: string;
};

/**
 * contains all the functions to render each type of organizer component in the config
 * @param field
 * @param groupIndex
 * @param fieldValue
 * @param handleFieldChange
 * @param disabled
 * @param errorMessage
 * @returns
 */
export const renderTextField = (
  fieldConfig: FieldConfig,
  handlerConfig: HandlerConfig
) => (
  <TextInput
    key={`${fieldConfig.field.name}-${handlerConfig.groupIndex}`}
    label={
      <span className="page-title">
        <p style={{ margin: 0, whiteSpace: "nowrap" }}>
          {fieldConfig.field.label}
        </p>
        {fieldConfig.field.info && fieldConfig?.popoverContent}
      </span>
    }
    description={fieldConfig.field.description}
    name={fieldConfig.field.name}
    value={fieldConfig.value}
    onChange={(e) =>
      handlerConfig.handleFieldChange(
        fieldConfig.field.name,
        e.target.value,
        handlerConfig.groupIndex
      )
    }
    placeholder={fieldConfig.field.placeholder}
    required={fieldConfig.field.required}
    disabled={handlerConfig.disabled}
    error={!handlerConfig.disabled && handlerConfig.errorMessage}
    className={fieldConfig?.className ?? "text-input"}
  />
);

/**
 * Render a long text field.
 * @param field - The field configuration.
 * @param groupIndex - The index of the group in the parent field.
 * @param fieldValue - The current value of the field.
 * @param handleFieldChange - The function to call when the field value changes.
 * @param disabled - Whether or not the field is disabled.
 * @param errorMessage - The error message to display if the field is invalid.
 * @param className - The class name to apply to the component.
 * @returns A Textarea component.
 */
export const renderLongTextField = (
  field: Field,
  groupIndex: number,
  fieldValue: any,
  handleFieldChange: Function,
  disabled: boolean,
  errorMessage: string,
  className?: string
) => (
  <Textarea
    placeholder={field.placeholder}
    autosize
    minRows={8}
    maxRows={8}
    mb="lg"
    value={fieldValue}
    onChange={(e) => handleFieldChange(field.name, e.target.value, groupIndex)}
    disabled={disabled}
    error={!disabled && errorMessage}
    label={field.label}
    description={field?.description}
    className={className ?? "long-input"}
    required={field.required}
  />
);

/**
 * Parse a date string in YYYY-MM-DD format to a Date object in local timezone.
 * This avoids timezone conversion issues by using the Date constructor with
 * explicit year, month, and day parameters.
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns Date object in local timezone or null if invalid
 */
const parseDateString = (dateStr: string): Date | null => {
  if (!dateStr || typeof dateStr !== "string") return null;
  const [year, month, day] = dateStr.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day); // Local timezone, no time component
};

/**
 * Parse a date string in MM/DD/YYYY format to a Date object in local timezone.
 * This avoids timezone conversion issues by using the Date constructor with
 * explicit year, month, and day parameters.
 * @param dateStr - Date string in MM/DD/YYYY format
 * @returns Date object in local timezone or null if invalid
 */
const parseMMDDYYYY = (dateStr: string): Date | null => {
  if (!dateStr || typeof dateStr !== "string") return null;
  const parts = dateStr.split("/");
  if (parts.length !== 3) return null;
  const [month, day, year] = parts.map(Number);
  if (!year || !month || !day) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return new Date(year, month - 1, day); // Local timezone, no time component
};

/**
 * Format a Date object to YYYY-MM-DD string.
 * @param date - Date object to format
 * @returns Date string in YYYY-MM-DD format
 */
const formatDateToString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Convert various date formats to a Date object for display.
 * Handles YYYY-MM-DD strings, Date objects, and ISO strings (legacy format).
 * Always returns dates in local timezone to avoid timezone conversion bugs.
 * @param fieldValue - Date value in various formats
 * @returns Date object in local timezone or null if invalid
 */
const validateAndConvertDate = (fieldValue: any): Date | null => {
  if (!fieldValue) return null;
  try {
    // If already a Date object, return as-is
    if (fieldValue instanceof Date) {
      return !isNaN(fieldValue.getTime()) ? fieldValue : null;
    }

    if (typeof fieldValue === "string") {
      // Normalize legacy ISO format (YYYY-MM-DDTHH:MM:SS.SSSZ → YYYY-MM-DD)
      const normalizedValue = fieldValue.includes("T")
        ? fieldValue.split("T")[0]
        : fieldValue;

      // Parse YYYY-MM-DD strings in local timezone (preferred format)
      if (/^\d{4}-\d{2}-\d{2}$/.test(normalizedValue)) {
        return parseDateString(normalizedValue);
      }
    }

    // Fallback: try parsing as ISO string or other formats
    const dateObj = new Date(fieldValue);
    return !isNaN(dateObj.getTime()) ? dateObj : null;
  } catch (error) {
    console.warn("Invalid date value:", fieldValue);
    return null;
  }
};

/**
 * Render a date field.
 * Stores dates as YYYY-MM-DD strings to avoid timezone conversion issues.
 * Birth dates and other calendar dates should be timezone-agnostic.
 * @param field - The field configuration.
 * @param groupIndex - The index of the group in the parent field.
 * @param fieldValue - The current value of the field (should be YYYY-MM-DD string or null).
 * @param handleFieldChange - The function to call when the field value changes.
 * @param disabled - Whether or not the field is disabled.
 * @param errorMessage - The error message to display if the field is invalid.
 * @returns A DateInput component.
 */
export const renderDateField = (
  field: Field,
  groupIndex: number,
  fieldValue: any,
  handleFieldChange: Function,
  disabled: boolean,
  errorMessage: string
) => {
  // Convert stored string value to Date object for display in DateInput
  const dateValue = validateAndConvertDate(fieldValue);

  return (
    <div className="date-picker-container">
      <p className="date-picker-label">
        {field.label} <span style={{ color: "red" }}>*</span>
      </p>
      <DateInput
        key={`${field.name}-${groupIndex}`}
        value={dateValue}
        valueFormat="MM/DD/YYYY"
        defaultLevel="year"
        onChange={(value: Date | string | null) => {
          let dateString: string | null = null;
          if (value) {
            if (value instanceof Date) {
              dateString = formatDateToString(value);
            } else if (typeof value === "string") {
              // Parse string in timezone-neutral way
              let parsedDate: Date | null = null;

              // Try MM/DD/YYYY format (Mantine's valueFormat)
              if (value.includes("/")) {
                parsedDate = parseMMDDYYYY(value);
              }
              // Try YYYY-MM-DD format
              else if (value.includes("-")) {
                parsedDate = parseDateString(value);
              }

              if (parsedDate && !isNaN(parsedDate.getTime())) {
                dateString = formatDateToString(parsedDate);
              }
            }
          }

          handleFieldChange(field.name, dateString as any, groupIndex);
        }}
        maxDate={new Date()}
        disabled={disabled}
        className={
          !disabled && errorMessage ? "error-picker date-picker" : "date-picker"
        }
      />
      <p className="error">{!disabled && errorMessage}</p>
    </div>
  );
};

/**
 * Render a password field.
 * @param field - The field configuration.
 * @param groupIndex - The index of the group in the parent field.
 * @param fieldValue - The current value of the field.
 * @param handleFieldChange - The function to call when the field value changes.
 * @param disabled - Whether or not the field is disabled.
 * @param errorMessage - The error message to display if the field is invalid.
 * @returns A MaskedPasswordInput component.
 */
export const renderPasswordField = (
  field: Field,
  groupIndex: number,
  fieldValue: any,
  handleFieldChange: Function,
  disabled: boolean,
  errorMessage: string
) => (
  <MaskedPasswordInput
    key={`${field.name}-${groupIndex}`}
    label={field.label}
    name={field.name}
    value={fieldValue}
    onChange={(e) =>
      handleFieldChange(
        field.name,
        (e.target as HTMLInputElement).value,
        groupIndex
      )
    }
    placeholder={field.placeholder}
    required={field.required}
    disabled={disabled}
    mask="000-00-0000"
    error={!disabled && errorMessage}
  />
);

/**
 * Render a number input field.
 *
 * @param {Field} field - The field configuration.
 * @param {number} groupIndex - The index of the group in the parent field.
 * @param {any} fieldValue - The current value of the field.
 * @param {Function} handleFieldChange - The function to call when the field value changes.
 * @param {boolean} disabled - Whether or not the field is disabled.
 * @param {string} errorMessage - The error message to display if the field is invalid.
 * @returns A NumberInput component.
 */

export const renderNumberField = (
  field: Field,
  groupIndex: number,
  fieldValue: any,
  handleFieldChange: Function,
  disabled: boolean,
  errorMessage: string
) => (
  <NumberInput
    key={`${field.name}-${groupIndex}`}
    label={field.label}
    name={field.name}
    value={fieldValue}
    min={field?.min ?? 0}
    max={field?.max}
    allowDecimal={field?.allowDecimal}
    onChange={(val) => handleFieldChange(field.name, val, groupIndex)}
    placeholder={field.placeholder}
    description={field?.description}
    required={field.required}
    className="number-input"
    disabled={disabled}
    error={!disabled && errorMessage}
  />
);

/**
 * Render a phone number field.
 * @param field - The field configuration.
 * @param groupIndex - The index of the group in the parent field.
 * @param fieldValue - The current value of the field.
 * @param handleFieldChange - The function to call when the field value changes.
 * @param disabled - Whether or not the field is disabled.
 * @param errorMessage - The error message to display if the field is invalid.
 * @returns A MaskedTextInput component.
 */
export const renderPhoneNumberField = (
  field: Field,
  groupIndex: number,
  fieldValue: any,
  handleFieldChange: Function,
  disabled: boolean,
  errorMessage: string
) => (
  <MaskedTextInput
    key={`${field.name}-${groupIndex}`}
    label={field.label}
    name={field.name}
    value={fieldValue}
    onChange={(e) =>
      handleFieldChange(
        field.name,
        (e.target as HTMLInputElement).value,
        groupIndex
      )
    }
    placeholder={field.placeholder}
    mask="(000) 000-0000"
    required={field.required}
    disabled={disabled}
    error={!disabled && errorMessage}
  />
);

/**
 * Render a select field.
 * @param field - The field configuration.
 * @param groupIndex - The index of the group in the parent field.
 * @param fieldValue - The current value of the field.
 * @param handleFieldChange - The function to call when the field value changes.
 * @param disabled - Whether or not the field is disabled.
 * @param errorMessage - The error message to display if the field is invalid.
 * @returns A Select component.
 */
export const renderSelectField = (
  field: Field,
  groupIndex: number,
  fieldValue: any,
  handleFieldChange: Function,
  disabled: boolean,
  errorMessage: string
) => (
  <div>
    {field.isDependentPage && (
      <p style={{ color: "#000", margin: "0.25rem 0rem 0.62rem 0rem" }}>
        {field.label}
      </p>
    )}
    <div className={field.isDependentPage ? "field-group two-column" : ""}>
      <Select
        key={`${field.name}-${groupIndex}`}
        name={field.name}
        value={fieldValue}
        onChange={(val) => handleFieldChange(field.name, val, groupIndex)}
        required={field.required}
        label={field.isDependentPage ? "" : field.label}
        data={field.options}
        searchable={true}
        disabled={disabled}
        error={!disabled && errorMessage}
        className="select-field"
      />
    </div>
  </div>
);

/**
 * Render a checkbox field.
 *
 * @param {Field} field - The field configuration.
 * @param {number} groupIndex - The index of the group in the parent field.
 * @param {any} fieldValue - The current value of the field.
 * @param {Function} handleFieldChange - The function to call when the field value changes.
 * @param {boolean} disabled - Whether or not the field is disabled.
 * @param {string} errorMessage - The error message to display if the field is invalid.
 * @returns A Checkbox component.
 */
export const renderCheckboxField = (
  field: Field,
  groupIndex: number,
  fieldValue: any,
  handleFieldChange: Function,
  disabled: boolean,
  errorMessage: string
) => (
  <Checkbox
    key={`${field.name}-${groupIndex}`}
    name={field.name}
    checked={fieldValue}
    onChange={() => handleFieldChange(field.name, !fieldValue, groupIndex)}
    label={field.label}
    disabled={disabled}
    error={!disabled && errorMessage}
  />
);

/**
 * Render a field of type 'checkboxChoice', which is a field where the user can
 * select one or more options from a list of predefined options. The options are
 * displayed as checkboxes, which can be clicked to select or deselect them.
 *
 * @param {Field} field - The field configuration.
 * @param {number} groupIndex - The index of the group in the parent field.
 * @param {any} fieldValue - The current value of the field.
 * @param {Function} handleChange - The function to call when the field value changes.
 * @param {boolean} disabled - Whether or not the field is disabled.
 * @param {string} errorMessage - The error message to display if the field is invalid.
 * @returns A div containing a paragraph with the label and a div with the checkboxes.
 */
export const renderCheckboxChoiceField = (
  field: Field,
  groupIndex: number,
  fieldValue: any,
  handleChange: Function,
  disabled: boolean,
  errorMessage: string
) => (
  <div key={`${field.name}-${groupIndex}`}>
    <p style={{ color: "#000", margin: "0rem 0rem 0.62rem 0rem" }}>
      {field.label}
    </p>
    <div
      style={{
        display: "flex",
        flexDirection: field.isRowLayout ? "row" : "column",
        gap: "0.5rem",
      }}
    >
      {field.options.map((option: any) => (
        <Checkbox
          key={option}
          onChange={() =>
            handleChange(field.name, option, field.multiselect, groupIndex)
          }
          checked={
            field.multiselect
              ? fieldValue?.includes(option)
              : fieldValue === option
          }
          radius="xl"
          label={option}
          disabled={disabled}
        />
      ))}
    </div>
    <p className="error">{!disabled && errorMessage}</p>
  </div>
);

type ChoiceFieldConfig = {
  field: Field;
  value: any;
  children?: ReactNode;
};

type ChoiceHandlerConfig = {
  groupIndex: number;
  handleChange: Function;
  disabled: boolean;
  errorMessage: string;
  renderFields: Function;
};

/**
 * Renders a field with a choice selection from a list of options.
 *
 * @param {ChoiceFieldConfig} fieldConfig - The field configuration.
 * @param {ChoiceHandlerConfig} handlerConfig - The handler configuration.
 * @returns {ReactElement} The rendered choice field component.
 */
export const renderChoiceField = (
  fieldConfig: ChoiceFieldConfig,
  handlerConfig: ChoiceHandlerConfig
) => {
  const renderOption = (option: ChoiceOption, optionIndex: number) => {
    const optionValue = typeof option === "string" ? option : option.value;
    const IconComponent = getIconComponent(option);
    const isChecked = fieldConfig.field.multiselect
      ? fieldConfig.value.includes(optionValue)
      : fieldConfig.value === optionValue;

    return (
      <div
        key={`${handlerConfig.groupIndex}-${optionValue}-${optionIndex}`}
        className={`choice-btn ${isChecked ? "checked" : ""}`}
        onClick={(e) => handleOptionClick(e, optionValue)}
        style={{ cursor: "pointer" }}
      >
        <label style={labelStyle}>
          <Checkbox
            checked={isChecked}
            onChange={() => {}}
            disabled={handlerConfig.disabled}
            radius="xl"
            size="md"
            style={{ pointerEvents: "none" }}
          />
          {IconComponent && (
            <span className="icon-wrapper">
              <IconComponent size={30} />
            </span>
          )}
          <span style={{ flexGrow: 1 }}>{optionValue}</span>
        </label>
      </div>
    );
  };

  /**
   * Renders additional fields based on the selected options.
   * For multi-select fields, it maps over the selected options and renders
   * the corresponding option fields. For single-select fields, it directly
   * renders the option fields for the selected value.
   *
   * @returns {JSX.Element | null} A JSX element containing the option fields
   * or null if no option fields are defined or no value is selected.
   */

  const renderOptionFields = () => {
    if (!fieldConfig.field.optionFields || !fieldConfig.value) return null;

    if (fieldConfig.field.multiselect) {
      return fieldConfig.value.map((selectedOption: any, index: number) => (
        <div
          key={`${selectedOption?.id}-${selectedOption}-${index}`}
          style={{ marginTop: "2rem" }}
        >
          {fieldConfig.field.optionFields[selectedOption]?.map(
            (cf: Field, i: number) => (
              <div key={`${cf.name}-${i}`} style={{ marginTop: "2rem" }}>
                {handlerConfig.renderFields([cf], handlerConfig.groupIndex)}
              </div>
            )
          )}
        </div>
      ));
    } else {
      return fieldConfig.field.optionFields[fieldConfig.value]?.map(
        (cf: Field, i: number) => (
          <div key={`${cf.name}-${i}`} style={{ marginTop: "2rem" }}>
            {handlerConfig.renderFields([cf], handlerConfig.groupIndex)}
          </div>
        )
      );
    }
  };

  /**
   * Handles the click event on an option field.
   * Prevents the default behavior (form submission) and calls the change handler
   * with the selected option value.
   *
   * @param {React.MouseEvent} e - The click event.
   * @param {string} optionValue - The value of the selected option.
   */
  const handleOptionClick = (e: React.MouseEvent, optionValue: string) => {
    e.preventDefault();
    handlerConfig.handleChange(
      fieldConfig.field.name,
      optionValue,
      fieldConfig.field.multiselect,
      handlerConfig.groupIndex,
      fieldConfig.field
    );
  };

  /**
   * Given a ChoiceOption, returns the TablerIcon component associated with that option,
   * or null if the option doesn't have an icon or if the icon is not a valid TablerIcon.
   * @param {ChoiceOption} option - The option to get an icon for.
   * @returns {React.ComponentType<{size?:number,style?:React.CSSProperties}> | null}
   */
  const getIconComponent = (option: ChoiceOption) => {
    if (typeof option === "object" && option.icon && TablerIcons[option.icon]) {
      return TablerIcons[option.icon] as React.ComponentType<{
        size?: number;
        style?: React.CSSProperties;
      }>;
    }
    return null;
  };

  const labelStyle = {
    display: "flex",
    alignItems: "center",
    width: "100%",
    cursor: "pointer",
    gap: "1rem",
  };

  return (
    <div
      key={`${fieldConfig.field.name}-${handlerConfig.groupIndex}`}
      className="choice-field"
    >
      <span className="field-label">
        <h3>{fieldConfig.field.label}</h3>
        {fieldConfig.children}
      </span>
      <p className="description">{fieldConfig.field.description}</p>
      <span className="choice-btns">
        {fieldConfig.field.options.map(renderOption)}
      </span>
      {renderOptionFields()}
      <p className="error">
        {!handlerConfig.disabled && handlerConfig.errorMessage}
      </p>
    </div>
  );
};

/**
 * Render a field of type 'tiles', which is a field where the user can select
 * one or more options from a list of predefined options. The options are
 * displayed as tiles, which can be clicked to select or deselect them.
 *
 * @param {Field} field - The field configuration.
 * @param {number} groupIndex - The index of the group in the parent field.
 * @param {any} fieldValue - The current value of the field.
 * @param {Function} handleFieldChange - The function to call when the field value changes.
 * @param {boolean} disabled - Whether or not the field is disabled.
 * @param {string} errorMessage - The error message to display if the field is invalid.
 */
export const renderTilesField = (
  field: Field,
  groupIndex: number,
  fieldValue: any,
  handleFieldChange: Function,
  disabled: boolean,
  errorMessage: string
) => {
  const toggleTile = (index: number) => {
    let newValues;
    const isLastTile = index === tiles.length - 1;
    const isChecked = fieldValue?.includes(index);

    if (isLastTile) {
      newValues = isChecked ? [] : [index];
    } else {
      const currentValues = fieldValue || [];
      if (isChecked) {
        newValues = currentValues.filter((v: number) => v !== index);
      } else {
        newValues = [
          ...currentValues.filter((v: number) => v !== tiles.length - 1),
          index,
        ];
      }
    }
    handleFieldChange(field.name, newValues, groupIndex);
  };

  return (
    <div
      key={`${field.name}-${groupIndex}`}
      className="tiles-field field-description"
    >
      <h3>{field.label}</h3>
      <p className="description">{field.description}</p>
      <div className="tiles-container">
        {tiles.map((t, index) => {
          const isChecked = fieldValue?.includes(index);

          return (
            <div
              key={t.id}
              className={`tile ${isChecked ? "checked" : ""}`}
              onClick={() => !disabled && toggleTile(index)}
            >
              <Checkbox
                onChange={() => toggleTile(index)}
                checked={isChecked}
                className="checkbox"
                size="md"
                disabled={disabled}
              />
              <img src={t.image} alt="tile" />
              <p className="description">{t.description}</p>
            </div>
          );
        })}
      </div>
      <p className="error">{!disabled && errorMessage}</p>
    </div>
  );
};

/**
 * Render a field of type 'revenueInput', which is a text input field for the user to
 * enter a revenue amount. The field displays a dollar sign on the left side of the input.
 *
 * @param {Field} field - The field configuration.
 * @param {number} groupIndex - The index of the group in the parent field.
 * @param {any} fieldValue - The current value of the field.
 * @param {Function} handleFieldChange - The function to call when the field value changes.
 * @param {boolean} disabled - Whether or not the field is disabled.
 * @param {string} errorMessage - The error message to display if the field is invalid.
 */
export const renderRevenueInputField = (
  field: Field,
  groupIndex: number,
  fieldValue: any,
  handleFieldChange: Function,
  disabled: boolean,
  errorMessage: string
) => (
  <div key={`${field.name}-${groupIndex}`} className="field-description">
    <p className="description">{field.description}</p>
    <NumberInput
      label={field.label}
      name={field.name}
      value={fieldValue}
      onChange={(value) =>
        // cast values to floats
        handleFieldChange(field.name, value, groupIndex)
      }
      placeholder={field.placeholder}
      required={field.required}
      leftSection={"$"}
      disabled={disabled}
      error={!disabled && errorMessage}
      decimalScale={2}
      fixedDecimalScale
      thousandSeparator=","
      min={0}
    />
  </div>
);

/**
 * Renders a dynamic input pair field using the DynamicInputPair component.
 *
 * @param {Field} field - The field configuration object containing details of the input pair.
 * @param {number} groupIndex - The index of the group to which this field belongs.
 * @param {string} parentObjectName - The name of the parent object in the data structure.
 * @param {boolean} disabled - Indicates whether the input pair should be disabled.
 * @param {any} errorMessages - An object containing error messages for the form fields.
 * @returns {JSX.Element} The rendered DynamicInputPair component.
 */

export const renderDynamicInputPairField = (
  field: Field,
  groupIndex: number,
  parentObjectName: string,
  disabled: boolean,
  errorMessages: any
) => (
  <DynamicInputPair
    key={`${field.name}-${groupIndex}`}
    selectLabel={field.selectLabel}
    inputLabel={field.inputLabel}
    isReverse={field.isReverse || false}
    selectData={field.selectData}
    isInputMonetary={field.isInputMonetary}
    storedObjectName={field.storedObjectName}
    groupIndex={groupIndex}
    parentObjectName={parentObjectName}
    isSelectInput={field.isSelectInput ?? true}
    disabled={disabled}
    errorMessages={errorMessages}
  />
);

/**
 * Renders a field of type 'differentDaycares', which is a custom component
 * DifferentDaycares. The component displays a group of three radio buttons
 * asking if the user has children in different daycares and if yes, then how
 * many different daycares. The field requires the user to enter a number of
 * different daycares.
 *
 * @param {Field} field - The field configuration object containing details
 * of the field.
 * @param {number} groupIndex - The index of the group to which this field
 * belongs.
 * @param {boolean} disabled - Indicates whether the field should be disabled.
 * @param {any} errorMessages - An object containing error messages for the
 * form fields.
 * @returns {JSX.Element} The rendered DifferentDaycares component.
 */
export const renderDifferentDaycaresField = (
  field: Field,
  groupIndex: number,
  disabled: boolean,
  errorMessages: any
) => (
  <DifferentDaycares
    key={`${field.name}-${groupIndex}`}
    groupIndex={groupIndex}
    disabled={disabled}
    labelOne={field.labelOne}
    labelTwo={field.labelTwo}
    labelThree={field.labelThree}
    parentObjectName="daycareInfo"
    errorMessages={errorMessages}
  />
);
